/**
 * Authentication Store (Zustand)
 *
 * Global state management for authentication:
 * - User session
 * - JWT tokens (access & refresh)
 * - Token persistence
 * - Auto token refresh
 * - Multi-tab synchronization
 *
 * @see WICL-DOC-002-A Authentication System
 */

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { authService, User } from "@/services/auth.service";
// Same direction as `authStore → auth.service → api.ts`, so this adds no cycle: api.ts reaches
// back through DOM events (`auth:token-refreshed`) rather than importing this store.
import { refreshAuthTokens, SessionRefusedError } from "@/lib/api";
import { jwtDecode } from "jwt-decode";
// Direct import is safe here: neither store it touches reaches a service, so nothing loops
// back to api.ts the way `authStore → auth.service → api.ts` does.
import { clearSessionData } from "./clearSessionData";

/**
 * JWT Token Payload
 */
interface JwtPayload {
  sub: string;
  email: string;
  exp: number;
  iat: number;
}

/**
 * Auth Store State
 */
interface AuthState {
  // State
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Token expiry tracking
  tokenExpiresAt: number | null;

  // Actions
  login: (
    email: string,
    password: string,
    rememberMe?: boolean,
  ) => Promise<void>;
  logout: () => void;
  refreshAccessToken: () => Promise<void>;
  updateUser: (user: Partial<User>) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  checkTokenExpiry: () => void;
  initializeAuth: () => void;
}

/**
 * Token expiry constants
 * Access token: 15 minutes
 * Refresh token: 30 days
 */
const REFRESH_THRESHOLD = 2 * 60 * 1000; // 2 minutes before expiry
const ACTIVITY_CHECK_INTERVAL = 60 * 1000; // Check every minute

/**
 * Decode JWT token to get expiry time
 */
const getTokenExpiry = (token: string): number | null => {
  try {
    const decoded = jwtDecode<JwtPayload>(token);
    return decoded.exp * 1000; // Convert to milliseconds
  } catch (error) {
    console.error("Failed to decode token:", error);
    return null;
  }
};

/**
 * Auth Store
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      tokenExpiresAt: null,

      /**
       * Login user
       */
      login: async (email: string, password: string, rememberMe = false) => {
        set({ isLoading: true, error: null });

        try {
          const response = await authService.login({
            email,
            password,
            rememberMe,
          });

          const tokenExpiresAt = getTokenExpiry(response.accessToken);

          set({
            user: response.user,
            accessToken: response.accessToken,
            refreshToken: response.refreshToken,
            isAuthenticated: true,
            isLoading: false,
            tokenExpiresAt,
          });

          // Start token refresh timer
          get().checkTokenExpiry();
        } catch (error: unknown) {
          const err = error as Error & {
            response?: { data?: { message?: string } };
          };
          set({
            error: err?.response?.data?.message || "Login failed",
            isLoading: false,
          });
          throw error;
        }
      },

      /**
       * Logout user
       */
      logout: () => {
        // Read before the state is cleared below — it is what the server revokes.
        const { refreshToken } = get();

        // Clear state first to prevent retry loops
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
          tokenExpiresAt: null,
        });

        // Clear localStorage (this will trigger storage event in other tabs)
        localStorage.removeItem("wicloud_auth");
        localStorage.removeItem("wicloud_tenant");

        // And everything else that belonged to that person. Signing out used to leave the
        // notification bell — amounts and balances included — for whoever signed in next on
        // the same browser.
        clearSessionData();

        // Revoke the session on the server (fire and forget). It used to post no body, after the
        // token was already gone from storage, so the server revoked nothing.
        authService.logout(refreshToken).catch((error) => {
          // Silently ignore logout API errors - we've already cleared local state
          if (import.meta.env.DEV) {
            console.debug(
              "Logout API call failed (expected if already logged out):",
              error,
            );
          }
        });
      },

      /**
       * Refresh access token
       */
      refreshAccessToken: async () => {
        const { refreshToken } = get();

        if (!refreshToken) {
          get().logout();
          return;
        }

        try {
          // One implementation for both refresh paths — this proactive timer and the 401
          // interceptor. They used to call /auth/refresh independently, so the two could send
          // the same token at once; the server rotates, so the loser was signed out. The shared
          // call de-duplicates within the tab and holds the cross-tab lock, and its
          // `auth:token-refreshed` event is what writes the new pair into this store.
          await refreshAuthTokens();

          console.log("[Auth] Token refreshed successfully");
        } catch (error) {
          console.error("[Auth] Token refresh failed:", error);
          // Only a refused token ends the session. A 5xx or a dropped connection leaves it in
          // place; the next tick, or the 401 interceptor, tries again.
          if (error instanceof SessionRefusedError) {
            get().logout();
          }
        }
      },

      /**
       * Update user data
       */
      updateUser: (userData: Partial<User>) => {
        set((state) => ({
          user: state.user ? { ...state.user, ...userData } : null,
        }));
      },

      /**
       * Set loading state
       */
      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      /**
       * Set error state
       */
      setError: (error: string | null) => {
        set({ error });
      },

      /**
       * Check token expiry and refresh if needed
       */
      checkTokenExpiry: () => {
        const { tokenExpiresAt, isAuthenticated, refreshAccessToken } = get();

        if (!isAuthenticated || !tokenExpiresAt) {
          return;
        }

        const now = Date.now();
        const timeUntilExpiry = tokenExpiresAt - now;

        // If token expires in less than REFRESH_THRESHOLD, refresh it
        if (timeUntilExpiry <= REFRESH_THRESHOLD) {
          console.log("[Auth] Token expiring soon, refreshing...");
          refreshAccessToken();
        }
      },

      /**
       * Initialize auth state
       * Sets up token refresh interval
       */
      initializeAuth: () => {
        const { checkTokenExpiry, isAuthenticated } = get();

        if (isAuthenticated) {
          // Check token expiry immediately
          checkTokenExpiry();

          // Set up interval to check token expiry
          const interval = setInterval(() => {
            checkTokenExpiry();
          }, ACTIVITY_CHECK_INTERVAL);

          // Clean up interval on window unload
          window.addEventListener("beforeunload", () => {
            clearInterval(interval);
          });
        }
      },
    }),
    {
      name: "wicloud_auth",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        tokenExpiresAt: state.tokenExpiresAt,
      }),
    },
  ),
);

/**
 * Multi-tab synchronization
 * Listen to storage events to sync auth state across tabs
 */
if (typeof window !== "undefined") {
  let isLoggingOut = false; // Flag to prevent logout loops

  const clearAuthState = () => {
    if (!isLoggingOut) {
      isLoggingOut = true;
      useAuthStore.setState({
        user: null,
        accessToken: null,
        refreshToken: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        tokenExpiresAt: null,
      });

      // The other two ways a session ends — an expired token the interceptor could not refresh,
      // and a sign-out in another tab — have to clear the same things as the button does.
      clearSessionData();
      setTimeout(() => {
        isLoggingOut = false;
      }, 1000);
    }
  };

  window.addEventListener("storage", (event) => {
    if (event.key === "wicloud_auth") {
      if (event.newValue === null) {
        // Auth cleared in another tab - sync logout
        clearAuthState();
      } else {
        // Auth updated in another tab - sync state
        try {
          const newState = JSON.parse(event.newValue);
          useAuthStore.setState({
            user: newState.state.user,
            accessToken: newState.state.accessToken,
            refreshToken: newState.state.refreshToken,
            isAuthenticated: newState.state.isAuthenticated,
            tokenExpiresAt: newState.state.tokenExpiresAt,
          });
        } catch (error) {
          console.error("Failed to sync auth state:", error);
        }
      }
    }
  });

  // Handle session expiry signalled by the API interceptor (401 + failed refresh).
  // Uses a DOM event rather than a direct import to avoid a circular dependency:
  //   api.ts → authStore.ts → auth.service.ts → api.ts
  window.addEventListener("auth:session-expired", () => {
    clearAuthState();
  });

  // WICL-404: the API interceptor refreshes tokens on a 401 and dispatches this event.
  // Sync the refreshed tokens into in-memory state so `tokenExpiresAt` stays accurate
  // (otherwise the proactive timer would fire a redundant refresh, and in rare cases a
  // stale expiry could mis-time future refreshes). Same-tab only; cross-tab sync uses
  // the `storage` listener above.
  window.addEventListener("auth:token-refreshed", ((event: CustomEvent) => {
    const detail = event.detail as
      | { accessToken?: string; refreshToken?: string }
      | undefined;
    if (!detail?.accessToken) {
      return;
    }
    useAuthStore.setState({
      accessToken: detail.accessToken,
      refreshToken: detail.refreshToken ?? useAuthStore.getState().refreshToken,
      tokenExpiresAt: getTokenExpiry(detail.accessToken),
    });
  }) as EventListener);

  // WICL-404: setInterval is throttled (<=1/min) or frozen when a tab is backgrounded,
  // so a near-expiry token may not be refreshed proactively. On refocus, check expiry
  // immediately — if the token already expired, the 401-interceptor refresh handles it
  // on the next request, but this catches the near-expiry window first.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      useAuthStore.getState().checkTokenExpiry();
    }
  });
}

/**
 * Initialize auth on app load — deferred to avoid state updates during render.
 * Using queueMicrotask ensures the store is fully hydrated by Zustand persist
 * before we check token expiry, and prevents "Cannot update a component
 * while rendering a different component" warnings.
 */
if (typeof window !== "undefined") {
  queueMicrotask(() => {
    useAuthStore.getState().initializeAuth();
  });
}
